import * as React from 'react';
import { ToastMessage } from "primereact/toast";
import { useDispatch } from 'react-redux';
import { ProjectDocFolderCUser } from '../../../workflows/models/ProjectDocFolderCUser';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { services } from '../../../../services';
import { alertError, alertErrors, alertSuccess } from '../../../../store/reducers/Alert';
import { DataTableComponent } from '../../../../components/datatables/DataTableComponent';
import { folderApproversListColumns } from './helpers/FolderApproversListColumns';
import { ProjectDocFolder } from '../../models/ProjectDocFolder';
import { NewCheckbox } from '../../../../components/synced/ui/NewCheckbox/NewCheckbox';
import { useIsMobile } from '../../../../helpers/hooks/useIsMobile';
import { UserSearchAutoComplete } from './helpers/UserSearchAutoComplete';
import { resolveFieldData } from '../../../../helpers/utils/FieldDataResolver';


interface FolderApproversListDefaultProps {
	projectDocFolderId: number;
	projectDocFolder: ProjectDocFolder;
	approvers: ProjectDocFolderCUser[];
	onUpdateAppovers: () => void;
}

export const FolderApproversListDefault: React.FC<FolderApproversListDefaultProps> = ({ projectDocFolderId, projectDocFolder, approvers, onUpdateAppovers }) => {
	const dispatch = useDispatch();
	const [promiseWrapper, loading] = useBackgroundPromiseWrapper();
	const isMobile = useIsMobile(900);

	const [data, setData] = React.useState<ProjectDocFolderCUser[]>(approvers);
	React.useEffect(() => {
		setData(approvers);
	}, [approvers]);

	const isEditMode: boolean = true;//canStartWorkflowInstance;

	const [originalValues, setOriginalValues] = React.useState<{ [id: number]: ProjectDocFolderCUser }>({});
	const [editModes, setEditModes] = React.useState<{ [id: number]: boolean }>({});
	const [checked, setChecked] = React.useState<boolean>(false);
	const getEditable = React.useCallback((item: ProjectDocFolderCUser): boolean => {
		if (!item?.Id)
			return true;

		return Boolean(editModes[item.Id]);
	}, [editModes]);


	const [hasNewRow, setHasNewRow] = React.useState<boolean>(false);
	const onAddRow = React.useCallback((): void => {
		const emptyVal = data.some(x => x.Id === 0);
		setHasNewRow(!emptyVal);
		if (emptyVal)
			setData(data.filter(x => x.Id !== 0));
		else
			setData([{
				Id: 0,
				ProjectDocFolderId: projectDocFolderId,
				UserId: 0,
			}, ...data]);
	}, [data, projectDocFolderId]);


	const onUpdate = React.useCallback((item: ProjectDocFolderCUser) => {
		let copyData = [...data];
		var idx = copyData.findIndex((value) => value.Id === item.Id);
		if (idx < 0)
			return;

		copyData[idx] = { ...item };
		setData(copyData);
	}, [data, originalValues]);

	const clearOriginal = React.useCallback((item: ProjectDocFolderCUser, acceptChanges: boolean): void => {
		if (item.Id === undefined || item.Id === 0)
			return;

		let originalValue = originalValues[item.Id];
		if (!originalValue)
			return;

		if (!acceptChanges)
			onUpdate(originalValue);

		let copyOriginalValues = { ...originalValues };
		delete (copyOriginalValues[item.Id]);
		setOriginalValues(copyOriginalValues);
	}, [originalValues, onUpdate]);

	const onEditableChanged = React.useCallback((item: ProjectDocFolderCUser, editable: boolean, acceptChanges: boolean = false): void => {
		let copyEditModes = { ...editModes };
		copyEditModes[item.Id || 0] = editable;
		setEditModes(copyEditModes);

		if (item.Id !== undefined && item.Id > 0) {
			if (editable) {
				let copyOriginalValues = { ...originalValues };
				copyOriginalValues[item.Id] = item;
				setOriginalValues(copyOriginalValues);
			} else
				clearOriginal(item, acceptChanges);
		}
	}, [editModes, originalValues, clearOriginal]);

	const onSave = React.useCallback((item: ProjectDocFolderCUser) => {
		if (Boolean(item.User?.Login)) {
			promiseWrapper(
				() => services.projectDocFolderCUserService.save([item]),
				(res: ProjectDocFolderCUser[]) => {
					if (item.Id === 0)
						setHasNewRow(false);

					onUpdateAppovers();
					onEditableChanged(res[0], false, true);
					dispatch(alertSuccess());
				}
			);
		}
		else {
			let messages: ToastMessage[] = [];
			if (!Boolean(item.User?.Login))
				messages.push({ summary: `Ошибка валидации`, detail: `Пользователь не выбран` });

			if (messages.length > 0)
				dispatch(alertErrors(messages));
		}
		// eslint-disable-next-line
	}, [onUpdateAppovers, onEditableChanged]);

	const onDelete = React.useCallback((item: ProjectDocFolderCUser) => {
		if (!item) {
			dispatch(alertError({ detail: `Approver is undefined` }));
			return;
		}

		promiseWrapper(
			() => services.projectDocFolderCUserService.delete([item?.Id ?? 0]),
			(res) => onUpdateAppovers()
		);
		// eslint-disable-next-line
	}, [onUpdateAppovers]);

	const promiseChecked = (e) => {
		setChecked(e);
		//console.log("e:", e);
		projectDocFolder.DisablePrimaryEmailNotification = e;
		projectDocFolder.ChangeChildren = [];
		projectDocFolder.Children = [];
		promiseWrapper(
			() => services.projectDocFolderService.save([projectDocFolder]),
			(res) => {
				//console.log("save");
			}
		);
	};

	return (
		<div className="notif-panel">
			<div className="notif-checkbox">
				<NewCheckbox
					checked={projectDocFolder.DisablePrimaryEmailNotification ?? false}
					onChange={e => promiseChecked(e)}
					label="Не уведомлять основной Email" />
			</div>

			{isMobile && (
				<>
					<div style={{ marginBottom: 10 }}>
						<button className="notif-action-btn" onClick={onAddRow}>
							<i className={hasNewRow ? "fa fa-times" : "fa fa-plus"} />
						</button>
					</div>
					<div className="notif-card-list">
						{data.map((row) => {
							const editable = getEditable(row);
							const isNew = row.Id === 0;
							const title = row?.User ? row.User.Title : "";
							const email = row?.User?.Email ?? "";

							if (isNew) {
								return (
									<div className="notif-card notif-card-new" key="new-row">
										<div className="notif-card-field">
											<span className="notif-card-label">Наименование</span>
											<UserSearchAutoComplete
												searchUser={resolveFieldData(row, "User") as any}
												onChange={(user: any) => onUpdate({
													...row,
													User: user,
													UserId: user?.Id || 0
												})}
												field="Title"
											/>
										</div>
										<div className="notif-card-actions-new">
											<button
												className="notif-action-btn notif-action-success"
												onClick={() => onSave(row)}>
												<i className="fa fa-save" />
											</button>
										</div>
									</div>
								);
							}

							return (
								<div className="notif-card" key={row.Id}>
									<div className="notif-card-top">
										<div className="notif-card-info">
											{editable ? (
												<UserSearchAutoComplete
													searchUser={resolveFieldData(row, "User") as any}
													onChange={(user: any) => onUpdate({
														...row,
														User: user,
														UserId: user?.Id || 0
													})}
													field="Title"
												/>
											) : (
												<>
													<div className="notif-card-title">{title}</div>
													{email && <div className="notif-card-email">{email}</div>}
												</>
											)}
										</div>
										<div className="notif-card-btns">
											<button
												className="notif-action-btn notif-action-danger"
												onClick={() => onDelete(row)}
												title={`Удалить ${title}`}>
												<i className="fa-regular fa-trash-can" />
											</button>
											{editable && (
												<button className="notif-action-btn notif-action-success" onClick={() => onSave(row)}>
													<i className="fa fa-check" />
												</button>
											)}
											{editable && (
												<button className="notif-action-btn" onClick={() => onEditableChanged(row, false)}>
													<i className="fa fa-times" />
												</button>
											)}
											{!editable && (
												<button className="notif-action-btn" onClick={() => onEditableChanged(row, true)}>
													<i className="fa-solid fa-pencil" />
												</button>
											)}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}

			{!isMobile && (
				<div className="notif-table">
					<DataTableComponent
						allColumns={folderApproversListColumns(isEditMode, hasNewRow, getEditable, onAddRow, onSave, onUpdate, onEditableChanged, onDelete)}
						data={data}
						paginator={false}
						loading={loading}
						tableName={`ApproversList`}
						className="p-datatable-sm" />
				</div>
			)}
		</div>
	);
}